<template>
  <div class="kanban-board">
    <div class="kanban-columns">
      <div
        v-for="status in projectStatuses"
        :key="status.value"
        class="kanban-column"
      >
        <div
          class="kanban-column-header"
          :style="{ borderTopColor: status.color }"
        >
          <h4 class="mb-0">
            {{ status.label }}
            <b-badge
              :variant="status.badgeVariant"
              pill
              class="mr-50"
            >
              {{ getProjectsByStatus(status.value).length }}
            </b-badge>
          </h4>
        </div>

        <vue-perfect-scrollbar
          :settings="perfectScrollbarSettings"
          class="kanban-column-content"
        >
          <draggable
            :list="getProjectsByStatus(status.value)"
            :group="{ name: 'projects' }"
            class="kanban-cards"
            ghost-class="ghost-card"
            :animation="200"
            @change="onDragChange($event, status.value)"
          >
            <b-card
              v-for="project in getProjectsByStatus(status.value)"
              :key="project.id"
              class="kanban-card mb-1 cursor-pointer"
              @click="openProjectDetails(project)"
            >
              <div class="d-flex justify-content-between align-items-center mb-75 position-relative">
                <div class="flex-grow-1 text-center">
                  <h5 class="mb-0">
                    {{ getProjectName(project) }}
                  </h5>
                </div>
                <b-dropdown
                  variant="link"
                  no-caret
                  toggle-class="p-0"
                  dropright
                  @click.stop
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="text-body"
                    />
                  </template>
                  <b-dropdown-item @click.stop="viewProjectDetails(project)">
                    <feather-icon
                      icon="EyeIcon"
                      size="16"
                      class="mr-50"
                    />
                    <!-- <span>عرض التفاصيل</span> -->
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="hasRole('admin')"
                    @click.stop="openAssignEmployeeModal(project)"
                  >
                    <feather-icon
                      icon="UserPlusIcon"
                      size="16"
                      class="mr-50"
                    />
                    <!-- <span>تخصيص لموظف</span> -->
                  </b-dropdown-item>
                </b-dropdown>
              </div>

              <p
                v-if="project.description"
                class="card-text mb-75"
              >
                {{ truncateText(project.description, 100) }}
              </p>

              <div
                v-if="project.service_type"
                class="d-flex flex-wrap mb-50"
              >
                <b-badge
                  variant="light-info"
                  pill
                  class="mr-50 mb-25"
                >
                  {{ getServiceTypeName(project) }}
                </b-badge>
              </div>

              <div class="d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center">
                  <small class="text-muted mr-1">
                    {{ formatDate(project.created_at) }}
                  </small>
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                    class="text-muted"
                  />
                </div>

                <div class="d-flex align-items-center">
                  <!-- Project Owner -->
                  <div
                    v-if="project.owners"
                    class="mr-1"
                  >
                    <small class="text-muted">
                      <b-badge variant="light-secondary">
                        {{ getProjectOwnerName(project) }}
                      </b-badge>
                    </small>
                  </div>
                  <!-- Assigned Employee -->
                  <div v-if="hasEmployee(project)">
                    <b-avatar
                      :text="avatarText(getEmployeeName(project))"
                      size="24"
                      variant="light-primary"
                    />
                  </div>
                </div>
              </div>

              <!-- <div class="mt-50" v-if="project.surveying_code || project.project_number || project.order_code">
                <b-badge variant="light-secondary">
                  {{ project.surveying_code ? project.surveying_code : project.project_number ? project.project_number : project.order_code }}
                </b-badge>
              </div> -->
            </b-card>
          </draggable>

          <div
            v-if="getProjectsByStatus(status.value).length === 0"
            class="text-center py-3"
          >
            <p class="text-muted mb-0">
              لا توجد مشاريع
            </p>
          </div>
        </vue-perfect-scrollbar>
      </div>
    </div>

    <!-- Admin Assign Employee Modal -->
    <b-modal
      id="assign-employee-modal"
      v-model="showAssignEmployeeModal"
      :title="`تخصيص المشروع: ${selectedProjectForAssign ? getProjectName(selectedProjectForAssign) : ''}`"
      ok-title="تخصيص"
      cancel-title="إلغاء"
      @ok="handleAssignEmployee"
      @cancel="resetAssignEmployeeForm"
      @hidden="resetAssignEmployeeForm"
    >
      <div
        v-if="loadingEmployees"
        class="text-center py-3"
      >
        <b-spinner
          variant="primary"
          label="جاري التحميل..."
        />
      </div>
      <div v-else>
        <b-form-group label="اختر الموظف *">
          <v-select
            v-model="selectedEmployeeId"
            :options="employees"
            :reduce="val => val.id"
            :get-option-label="option => `${option.full_name}${option.employee_code ? ' - ' + option.employee_code : ''}`"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            placeholder="اختر الموظف"
            @search="onEmployeeSearch"
          >
            <template #option="option">
              <span>{{ option.full_name }}{{ option.employee_code ? ' - ' + option.employee_code : '' }}</span>
            </template>
            <template #selected-option="option">
              <span>{{ option.full_name }}{{ option.employee_code ? ' - ' + option.employee_code : '' }}</span>
            </template>
          </v-select>
        </b-form-group>
        <b-alert
          variant="info"
          show
          class="mt-2"
        >
          <small>سيتم تخصيص المشروع للموظف المحدد بحالة "قيد الانتظار"</small>
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BBadge,
  BAvatar,
  BDropdown,
  BDropdownItem,
  BModal,
  BFormGroup,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import draggable from 'vuedraggable'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import { avatarText } from '@core/utils/filter'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ProjectAssignmentsKanbanView',
  components: {
    BCard,
    BBadge,
    BAvatar,
    BDropdown,
    BDropdownItem,
    BModal,
    BFormGroup,
    BAlert,
    BSpinner,
    vSelect,
    draggable,
    VuePerfectScrollbar,
  },
  props: {
    projects: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showAssignEmployeeModal: false,
      selectedProjectForAssign: null,
      selectedEmployeeId: null,
      employees: [],
      loadingEmployees: false,
      assigningEmployee: false,
      searchTimeout: null,
      projectStatuses: [
        {
          value: 'pending',
          label: 'قيد الانتظار',
          color: '#FF9F43',
          badgeVariant: 'light-warning',
        },
        {
          value: 'in_progress',
          label: 'قيد التنفيذ',
          color: '#00CFE8',
          badgeVariant: 'light-info',
        },
        {
          value: 'reviewing',
          label: 'قيد المراجعة',
          color: '#06284D',
          badgeVariant: 'light-primary',
        },
        {
          value: 'completed',
          label: 'مكتملة',
          color: '#28C76F',
          badgeVariant: 'light-success',
        },
      ],
      perfectScrollbarSettings: {
        maxScrollbarLength: 150,
        wheelPropagation: false,
      },
    }
  },
  methods: {
    avatarText,
    hasRole(role) {
      const userRoles = this.$store.getters['users/user_roles']
      if (Array.isArray(userRoles)) {
        return userRoles.includes(role)
      }
      return userRoles && Object.values(userRoles).includes(role)
    },
    getProjectsByStatus(status) {
      return this.projects.filter(project => {
        const assignmentStatus = (project.assignment && project.assignment.status) || project.status
        return assignmentStatus === status
      })
    },
    getProjectName(project) {
      // Display project name with project_number if available (as per guide)
      if (project.name && (project.surveying_code || project.project_number)) {
        return `${project.name} (${project.surveying_code ? project.surveying_code : project.project_number})`
      }
      return project.name || project.surveying_code || project.project_number || project.order_code || `مشروع #${project.id}`
    },
    getProjectOwnerName(project) {
      if (project.owners) {
        return project.owners[0].name || project.owners[0].full_name || project.owners[0].username || 'N/A'
      }
      console.log('project.owners', project.owners)
      return 'N/A'
    },
    getServiceTypeName(project) {
      if (project.service_type) {
        return project.service_type.name_ar || project.service_type.name || 'N/A'
      }
      return 'N/A'
    },
    hasEmployee(project) {
      return project.assignment && project.assignment.employee
    },
    getEmployeeName(project) {
      if (project.assignment && project.assignment.employee) {
        return project.assignment.employee.full_name
               || project.assignment.employee.name
               || ''
      }
      return ''
    },
    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? `${text.substring(0, length)}...` : text
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },
    async onDragChange(event, newStatus) {
      if (event.added) {
        const project = event.added.element
        const oldStatus = (project.assignment && project.assignment.status) || project.status

        // Optimistically update the UI
        if (project.assignment) {
          project.assignment.status = newStatus
        } else {
          project.status = newStatus
        }

        try {
          let result
          const projectId = project.id

          // Call appropriate API based on target status
          if (newStatus === 'in_progress') {
            result = await this.$store.dispatch('projects/moveToInProgress', {
              projectId,
            })
          } else if (newStatus === 'reviewing') {
            result = await this.$store.dispatch('projects/moveToReview', {
              projectId,
            })
          } else if (newStatus === 'completed') {
            result = await this.$store.dispatch('projects/moveToCompleted', {
              projectId,
            })
          } else {
            throw new Error('Invalid status transition')
          }

          // Update project with response data if available
          if (result && result.data && result.data.assignment) {
            if (!project.assignment) {
              project.assignment = {}
            }
            Object.assign(project.assignment, result.data.assignment)

            // Update status in the assignment
            project.assignment.status = newStatus
          } else {
            // If no assignment in response, update status directly
            if (project.assignment) {
              project.assignment.status = newStatus
            } else {
              project.status = newStatus
            }
          }

          // Emit update to parent to save to localStorage (without triggering full reload)
          this.$emit('project-updated', project)

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'تم تحديث الحالة',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'تم تحديث حالة المشروع بنجاح',
            },
          })

          // Don't emit refresh to avoid showing spinner - update is already handled via project-updated
        } catch (error) {
          // Revert on error
          if (project.assignment) {
            project.assignment.status = oldStatus
          } else {
            project.status = oldStatus
          }

          // Use error handler utility
          let errorMessage = 'فشل تحديث حالة المشروع'
          if (error && error.response) {
            const messages = {
              employee_not_found: 'الموظف غير موجود',
              project_not_assigned_to_employee: 'المشروع غير مخصص لك',
              project_must_be_in_progress: 'المشروع يجب أن يكون قيد العمل',
              project_must_be_in_review: 'المشروع يجب أن يكون قيد المراجعة',
            }
            const message = error.response.data && error.response.data.message
            errorMessage = messages[message] || message || errorMessage
          } else if (error && error.message) {
            errorMessage = error.message
          }

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'خطأ',
              icon: 'XIcon',
              variant: 'danger',
              text: errorMessage,
            },
          })
        }
      }
    },
    openProjectDetails(project) {
      this.$router.push({ name: 'project_details', params: { id: project.id } })
    },
    viewProjectDetails(project) {
      this.$router.push({ name: 'project_details', params: { id: project.id } })
    },
    openAssignEmployeeModal(project) {
      this.selectedProjectForAssign = project
      this.showAssignEmployeeModal = true
      this.loadEmployees()
    },
    async loadEmployees() {
      this.loadingEmployees = true
      try {
        // Load employees using the store
        await this.$store.dispatch('employees/listEmployees', {
          query: {
            per_page: 1000,
            status: 'contracted', // Only active/contracted employees
          },
        })
        const employeesData = this.$store.getters['employees/employees']
        if (employeesData && employeesData.data) {
          this.employees = Array.isArray(employeesData.data) ? employeesData.data : []
        } else if (Array.isArray(employeesData)) {
          this.employees = employeesData
        } else {
          this.employees = []
        }
      } catch (error) {
        console.error('Error loading employees:', error)
        this.employees = []
      } finally {
        this.loadingEmployees = false
      }
    },
    onEmployeeSearch(search, loading) {
      if (search) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => {
          loading(true)
          this.$store.dispatch('employees/listEmployees', {
            query: {
              search,
              per_page: 50,
            },
          }).then(() => {
            const employeesData = this.$store.getters['employees/employees']
            if (employeesData && employeesData.data) {
              this.employees = Array.isArray(employeesData.data) ? employeesData.data : []
            } else if (Array.isArray(employeesData)) {
              this.employees = employeesData
            }
            loading(false)
          }).catch(() => {
            loading(false)
          })
        }, 300)
      }
    },
    async handleAssignEmployee() {
      if (!this.selectedEmployeeId || !this.selectedProjectForAssign) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'تحذير',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'يرجى اختيار موظف',
          },
        })
        return false // Prevent modal from closing
      }

      this.assigningEmployee = true
      try {
        const result = await this.$store.dispatch('projects/adminAssignSingleProject', {
          projectId: this.selectedProjectForAssign.id,
          employeeId: this.selectedEmployeeId,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'نجح',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'تم تخصيص المشروع للموظف بنجاح',
          },
        })

        // Update project with assignment info if available
        if (result && result.data) {
          if (!this.selectedProjectForAssign.assignment) {
            this.selectedProjectForAssign.assignment = {}
          }
          Object.assign(this.selectedProjectForAssign.assignment, result.data)
          this.selectedProjectForAssign.assignment.status = 'pending'

          // Emit event to remove project from the list after assignment
          this.$emit('project-assigned', this.selectedProjectForAssign)
        }

        this.resetAssignEmployeeForm()
        return true // Allow modal to close
      } catch (error) {
        const errorMessage = this.handleAssignError(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'خطأ',
            icon: 'XIcon',
            variant: 'danger',
            text: errorMessage,
          },
        })
        return false // Prevent modal from closing on error
      } finally {
        this.assigningEmployee = false
      }
    },
    resetAssignEmployeeForm() {
      this.selectedEmployeeId = null
      this.selectedProjectForAssign = null
    },
    handleAssignError(error) {
      if (!error || !error.response) {
        return 'خطأ في الاتصال بالخادم'
      }

      const { status } = error.response
      const message = error.response.data && error.response.data.message

      if (status === 400) {
        if (message === 'project_already_assigned_to_another_employee') {
          return 'المشروع مخصص بالفعل لموظف آخر. يرجى إلغاء التخصيص الحالي أولاً.'
        }
        return message || 'طلب غير صحيح'
      }

      if (status === 404) {
        return 'المشروع أو الموظف غير موجود'
      }

      if (status === 422) {
        return 'البيانات المدخلة غير صحيحة'
      }

      if (status === 500) {
        return 'خطأ في الخادم. يرجى المحاولة لاحقاً'
      }

      return message || 'حدث خطأ غير متوقع'
    },
  },
}
</script>

<style lang="scss" scoped>
.kanban-board {
  width: 100%;
  overflow-x: auto;
  padding-bottom: 1rem;
}

.kanban-columns {
  display: flex;
  gap: 1.5rem;
  min-height: calc(100vh - 300px);
}

.kanban-column {
  min-width: 250px;
  max-width: 280px;
  flex: 1;
  background-color: #f8f8f8;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
}

.kanban-column-header {
  padding: 1rem;
  border-top: 3px solid;
  border-radius: 0.5rem 0.5rem 0 0;
  background-color: white;

  h4 {
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

.kanban-column-content {
  flex: 1;
  padding: 1rem;
  max-height: calc(100vh - 350px);
  overflow-y: auto;
}

.kanban-cards {
  min-height: 100px;
}

.kanban-card {
  cursor: grab;
  transition: all 0.2s ease;
  border: 1px solid #e9ecef;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    cursor: grabbing;
  }
}

.ghost-card {
  opacity: 0.5;
  background: #f8f9fa;
}

.kanban-dropdown {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}

@media (max-width: 768px) {
  .kanban-columns {
    flex-direction: column;
  }

  .kanban-column {
    max-width: 100%;
    min-width: 100%;
  }
}
</style>
